import { getInfCLogger } from '@infodata-group/inf-commons-core';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { RestService } from '@infodata-group/inf-commons';
import { filter, map } from 'rxjs/operators';
import { Urgency } from '../model/urgency';
import { UrgencyFactory } from '../factory/urgency-factory';

const logger = getInfCLogger('UrgencyService');

@Injectable({
  providedIn: 'root',
})
export class UrgencyService {
  private readonly restService = inject(RestService);
  public readonly factory = new UrgencyFactory();
  private readonly delay = 1000 * 60 * 5;

  public colors: {
    id: string;
    className: string;
  }[] = [];

  public emergencies$$ = new BehaviorSubject<Urgency[]>([]);
  private loadedEmergenciesTimestamp$$ = new BehaviorSubject<null | number>(
    null,
  );
  public forceNextRequest = false;

  public nothingUrgency = new Urgency('-', '', -1);

  public get canActivate$(): Observable<boolean> {
    return this.loadedEmergenciesTimestamp$$.pipe(
      filter((timestamp) => timestamp !== null),
      map((timestamp) => timestamp !== null),
    );
  }

  public shouldSendHttpRequest(): boolean {
    if (this.forceNextRequest) {
      this.forceNextRequest = false;
      return true;
    }
    const alreadyLoaded = this.emergencies$$.getValue().length > 0;
    const delta =
      Date.now() - (this.loadedEmergenciesTimestamp$$.getValue() || 0);
    return !alreadyLoaded || delta > this.delay;
  }

  private requestEmergencies$(): Observable<Urgency[]> {
    if (!this.shouldSendHttpRequest()) {
      return this.emergencies$$.asObservable();
    }
    return this.restService.getAll(this.factory).pipe(
      tap((emergencies) => {
        emergencies.forEach((emergency) => {
          this.createCssSelector(emergency['color']);
        });
        // if no urgency
        this.createCssSelector(this.nothingUrgency.color);
        this.emergencies$$.next(emergencies);
        this.loadedEmergenciesTimestamp$$.next(Date.now());
      }),
    );
  }

  public loadEmergencies$(force?: boolean): Observable<Urgency[]> {
    if (force) {
      this.forceNextRequest = true;
    }
    return this.requestEmergencies$();
  }

  public getFormatter() {
    const formatter = {
      type: 'icon',
      displayText: false,
      field: 'urgence',
      values: {},
    };
    const emergencies = this.emergencies$$.getValue();
    emergencies.forEach((emergency) => {
      const colorClass = this.colors.find((c) => c.id === emergency['color']);
      if (colorClass) {
        formatter.values[emergency.id] =
          `${emergency.iconTable} fas ${colorClass.className}`;
      }
    });
    // if no urgency
    this.nothingUrgency.iconTable =
      emergencies.length > 0 ? emergencies[0].iconTable : 'burn';
    formatter.values[this.nothingUrgency.id] =
      `${this.nothingUrgency.iconTable} fas generated-color-${this.nothingUrgency.color}`;
    return formatter;
  }

  private createCssSelector(color: string) {
    try {
      const c = {
        id: `${color}`,
        className: `generated-color-${color}`,
      };
      const stylesheet = document.styleSheets[2];
      const rule = `.${c.className} { color: #${c.id} }`;
      stylesheet.insertRule(rule, stylesheet.cssRules.length);
      this.colors.push(c);
    } catch (e) {
      logger.error(`Can't create color '${color}'`, { error: e });
    }
  }
}
